import React from 'react'
import { useDebounce } from '../../../utils/hooks'
import CustomCheckboxGroup from './CustomCheckboxGroup'
import { FilterPanel } from './FilterPanel'

interface CheckboxGroupFilterOption<T extends string | number> {
  label: string
  value: T
}

interface CheckboxGroupFilterProps<T extends string | number> {
  options: CheckboxGroupFilterOption<T>[]
  value?: T[]
  onChange?: (values: T[]) => void
  checkOnSearch?: boolean
  placeholder?: string
}

/** Convenience wrapper: FilterPanel + CustomCheckboxGroup */
const CheckboxGroupFilter = <T extends string | number>({
  options,
  value = [],
  onChange,
  checkOnSearch = false,
  placeholder
}: CheckboxGroupFilterProps<T>) => {
  const [searchText, setSearchText] = React.useState('')
  const debouncedSearchText = useDebounce(searchText, 300)

  const handleSearch = () => {
    const query = searchText?.trim()
    if (checkOnSearch && query) {
      const lower = query.toLowerCase()
      const matchedValues = options.filter(o => o.label.toLowerCase().includes(lower)).map(o => o.value)
      onChange?.(Array.from(new Set([...value, ...matchedValues])) as T[])
    }
  }

  return (
    <FilterPanel
      searchText={searchText}
      onSearchChange={setSearchText}
      onSearch={handleSearch}
      selectedCount={value.length}
      totalCount={options.length}
      onClear={() => onChange?.([])}
      showEnterHint={checkOnSearch}
      placeholder={placeholder}
    >
      <CustomCheckboxGroup
        height={300}
        options={options}
        value={value}
        onChange={onChange}
        searchText={debouncedSearchText}
      />
    </FilterPanel>
  )
}

export default CheckboxGroupFilter
